import type { ModuleSchema } from './registry';
import { createButton, createSlider, getAppQuality, getGlobalSliderMode, setAppQuality, setGlobalSliderMode, type AppQuality } from './ui-controls';
import { API_BASE, apiAuthHeaders } from './api-client';

// ---- Export Format State (3MF default vs STL) ----
let activeExportFormat: '3mf' | 'stl' = '3mf';
const exportFormatListeners: Array<(fmt: '3mf' | 'stl') => void> = [];

export function getExportFormat(): '3mf' | 'stl' {
  return activeExportFormat;
}

export function setExportFormat(fmt: '3mf' | 'stl'): void {
  activeExportFormat = fmt;
  exportFormatListeners.forEach(cb => cb(fmt));
}

export function onExportFormatChange(cb: (fmt: '3mf' | 'stl') => void): void {
  exportFormatListeners.push(cb);
}

// ---- Sidebar renderer — generic theo ModuleSchema ----
export interface SidebarHandle {
  root: HTMLElement;
  status: { el: HTMLElement; set: (message: string) => void; setError: (message: string) => void; setOk: (message: string) => void } | null;
}

export function renderSidebar(
  module: ModuleSchema,
  sidebarEl: HTMLElement,
  onSliderChange?: (key: string, value: number | string) => void,
  // TASK 19 fix (2026-09-30): initial values for the controls.
  //
  // The bug: main.ts restores saved params into `currentParams` correctly, but this function
  // built every slider from `slider.default` and ignored that object entirely - so the restored
  // values were never shown. Measured in Chrome: change baseDia to 123, leave the editor, come
  // back - localStorage still held 123 but the slider read 110 again.
  //
  // Passing the values in (rather than reading localStorage here) keeps this function free of
  // storage concerns and lets main.ts decide precedence: saved value if present, else default.
  initialValues?: Record<string, any>,
): SidebarHandle {
  sidebarEl.innerHTML = '';
  
  // R24 (2026-09-22): Sidebar 3-zone layout: Header (fixed) | Scrollable content | Footer (fixed)
  // Inspired by Blender/Rhino/CAD software UI pattern
  
  sidebarEl.style.display = 'flex';
  sidebarEl.style.flexDirection = 'column';
  sidebarEl.style.height = '100%';
  sidebarEl.style.overflow = 'hidden';  // Parent doesn't scroll, zones do

  // ---- HEADER ZONE (fixed) ----
  const headerZone = document.createElement('div');
  headerZone.style.cssText = `
    flex-shrink: 0;
    padding: 12px 15px;
    border-bottom: 1px solid var(--border);
    background: var(--bg-surface);
  `;

  // Home link (← Home)
  // TASK 19 (2026-09-30): this used to be `href="/"`, i.e. a full page navigation. That
  // reloaded the entire app just to show the home screen, which is slow and threw away all
  // in-memory state. It now changes the hash instead, which the router already handles by
  // calling showHome() in place. Params are persisted separately (see main.ts) so they
  // survive either way, but not reloading is what makes the transition instant.
  const isCoreModule = module.category === 'core_geometry' || module.id.startsWith('core_');
  const homeLink = document.createElement('a');
  homeLink.href = isCoreModule ? '#/core' : '#';
  homeLink.textContent = isCoreModule ? '← Core Library' : '← Home';
  homeLink.style.cssText = `
    display: inline-block;
    font-size: 11px;
    color: var(--accent);
    text-decoration: none;
    margin-bottom: 8px;
    cursor: pointer;
    transition: all 0.15s;
  `;
  homeLink.addEventListener('click', (e) => {
    // Prevent the browser from following '#' and leave the routing to the hashchange
    // listener, so there is exactly one code path into showHome().
    e.preventDefault();
    window.location.hash = isCoreModule ? '#/core' : '';
  });
  homeLink.addEventListener('mouseenter', () => homeLink.style.opacity = '0.7');
  homeLink.addEventListener('mouseleave', () => homeLink.style.opacity = '1');
  headerZone.appendChild(homeLink);

  // Module label + description
  const title = document.createElement('h2');
  title.textContent = module.label;
  title.style.cssText = 'font-size: 16px; font-weight: 600; margin: 12px 0 8px 0; color: var(--text);';
  headerZone.appendChild(title);

  // Global slider mode toggle + reset button (below title)
  // TASK 28 fix: the toggle is on its OWN row so its longer, self-explanatory label fits.
  // Squeezed next to "Reset All" it had ~113px and had to be abbreviated into something
  // unrecognisable.
  const buttonRow = document.createElement('div');
  buttonRow.style.cssText = 'display:flex;flex-direction:column;gap:6px;margin-top:6px;';

  // TASK 28 (2026-09-30, Nam's decision): SEGMENTED two-half switch, not a single toggling button.
  //
  // History of this control:
  //   1. One button, 9px, transparent, grey-on-white -> the user reported "I don't see the
  //      toggle". Measured in Chrome: 113x20 px, `background: rgba(0,0,0,0)`.
  //   2. One button, 11px, bordered, self-describing label -> visible, but still a single
  //      control that only shows the CURRENT state. To learn what the other option is, you have
  //      to click it.
  //   3. (this) Two halves, BOTH options always on screen. The active half is filled with the
  //      accent colour, the inactive half is muted. Nothing has to be remembered or discovered.
  //
  // A second benefit the spec calls out: the switch doubles as a SIGNAL. A user who drags a
  // slider and sees no change can glance up, see MANUAL lit, and understand immediately - rather
  // than concluding the app is broken.
  //
  // Real <button> elements, not divs, so Enter/Space work without extra key handling.
  const segmented = document.createElement('div');
  segmented.style.cssText = [
    'display:flex', 'width:100%', 'margin-top:6px',
    'border:1px solid var(--border)', 'border-radius:5px', 'overflow:hidden',
  ].join(';');

  const manualHalf = document.createElement('button');
  manualHalf.type = 'button';
  manualHalf.textContent = '✓ MANUAL';

  const realtimeHalf = document.createElement('button');
  realtimeHalf.type = 'button';
  realtimeHalf.textContent = '⚡ REAL-TIME';

  // 28px tall, 12px text - the spec's target size, chosen so it is readable at a glance.
  for (const half of [manualHalf, realtimeHalf]) {
    half.style.cssText = [
      'flex:1', 'height:28px', 'font-size:12px', 'font-weight:600',
      'font-family:inherit', 'border:none', 'border-radius:0',
      'cursor:pointer', 'transition:background 0.15s ease, color 0.15s ease',
      'padding:0', 'line-height:1',
    ].join(';');
  }

  /** Paint both halves. Always repaint BOTH - the previous code only styled the button it
   *  had just changed, which is why a half-styled state could survive a mode change.
   *
   *  NOTE (found in Chrome, 2026-09-30): the first version of this used
   *  `half.style.cssText.replace(/background:[^;]*;color:[^;]*;/, ...)`. That silently did
   *  nothing: reading `cssText` back gives the browser's NORMALISED form (spaces after colons,
   *  colours expanded to rgb()), so the regex never matched and both halves stayed filled with
   *  the accent colour. Measured: both halves `rgb(26,26,26)`, so the switch showed no state at
   *  all. Setting the two properties directly cannot go wrong that way. */
  const paintSegmented = () => {
    const isRealtime = getGlobalSliderMode();
    const paint = (el: HTMLButtonElement, active: boolean) => {
      el.style.background = active ? '#1a1a1a' : '#f8fafc';
      el.style.color = active ? '#ffffff' : '#64748b';
      el.setAttribute('aria-pressed', String(active));
    };
    paint(manualHalf, !isRealtime);
    paint(realtimeHalf, isRealtime);
  };

  /** Shared handler: both halves set the mode, then everything is repainted from state. */
  const applyMode = (realtime: boolean) => {
    setGlobalSliderMode(realtime);
    paintSegmented();
    // Sliders carry their own mode observer (button visibility / disabled state).
    scrollZone.querySelectorAll('.control-group').forEach((group: any) => {
      group._modeObserver?.update();
    });
  };

  manualHalf.addEventListener('click', () => applyMode(false));
  realtimeHalf.addEventListener('click', () => applyMode(true));

  segmented.appendChild(manualHalf);
  segmented.appendChild(realtimeHalf);
  paintSegmented();

  const qualityLabel = document.createElement('div');
  qualityLabel.textContent = 'APP QUALITY · preview only';
  qualityLabel.style.cssText = 'font-size:10px;color:var(--text-muted);font-weight:600;margin-top:8px;';
  const qualityRow = document.createElement('div');
  qualityRow.style.cssText = 'display:flex;width:100%;border:1px solid var(--border);border-radius:5px;overflow:hidden;';
  const qualityButtons: Array<{ key: AppQuality; label: string }> = [
    { key: 'low', label: 'DRAFT' }, { key: 'medium', label: 'STANDARD' }, { key: 'high', label: 'HIGH' },
  ];
  const qualityTriangleBudget: Record<AppQuality, string> = { low: '~35% triangles', medium: '~60% triangles', high: '100% triangles' };
  const paintQuality = () => { const active = getAppQuality(); qualityRow.querySelectorAll('button').forEach((button) => { const isActive = button.dataset.quality === active; const detail = button.querySelector('small'); if (detail) detail.textContent = isActive && button.dataset.actual ? button.dataset.actual : button.dataset.budget || ''; button.style.background = isActive ? '#1a1a1a' : '#f8fafc'; button.style.color = isActive ? '#fff' : '#64748b'; button.setAttribute('aria-pressed', String(isActive)); }); };
  qualityButtons.forEach(({ key, label }) => { const button = document.createElement('button'); button.type = 'button'; button.innerHTML = `${label}<small>${qualityTriangleBudget[key]}</small>`; button.dataset.quality = key; button.dataset.budget = qualityTriangleBudget[key]; button.title = `${label}: ${qualityTriangleBudget[key]}`; button.style.cssText = 'flex:1;height:40px;border:0;border-radius:0;font-size:10px;font-weight:600;cursor:pointer;padding:3px 2px;line-height:1.1;'; const detail = button.querySelector('small')!; detail.style.cssText = 'display:block;font-size:8px;font-weight:400;opacity:.8;margin-top:3px;'; button.addEventListener('click', () => { setAppQuality(key); paintQuality(); }); qualityRow.appendChild(button); });
  paintQuality();

  // Global reset button
  const resetAll = document.createElement('button');
  resetAll.textContent = '↺ Reset All';
  // TASK 28 fix: matched to the toggle so the two controls read as one group instead of one
  // prominent button next to a barely-visible one.
  resetAll.style.cssText = `
    flex: 1;
    font-size: 11px;
    font-weight: 600;
    padding: 7px 10px;
    border: 1px solid var(--border);
    background: #ffffff;
    color: var(--text);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
  `;

  resetAll.addEventListener('click', () => {
    // Reset all sliders to default - realtime update model
    const allSliders = scrollZone.querySelectorAll('.control-group');
    allSliders.forEach((group: any) => {
      const sliderKey = group.dataset.sliderKey;
      if (sliderKey) {
        const slider = module.sliders.find(s => s.key === sliderKey);
        if (slider) {
          const inputs = group.querySelectorAll('input[type="range"], input[type="number"]');
          if (inputs.length >= 2) {
            const rangeInput = inputs[0] as HTMLInputElement;
            const numberInput = inputs[1] as HTMLInputElement;
            rangeInput.value = String(slider.default);
            numberInput.value = String(slider.default);
            // Trigger callback to update model
            if (onSliderChange) {
              onSliderChange(sliderKey, slider.default);
            }
          }
        }
      }
    });
  });

  // Maintenance controls: clear only browser-side preview/thumbnail caches.
  // User parameters and saved presets are intentionally kept.
  const clearBrowserCache = async (): Promise<void> => {
    try {
      const keys = Object.keys(localStorage);
      for (const key of keys) {
        if (key.startsWith('boxed3d_thumbnails_') || key.startsWith('boxed3d_preview_')) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // Private browsing can deny storage access; cache API cleanup still helps.
    }
    try {
      if ('caches' in window) {
        const cacheKeys = await window.caches.keys();
        await Promise.all(cacheKeys.map(key => window.caches.delete(key)));
      }
    } catch {
      // CacheStorage is optional and may be unavailable in local development.
    }
    // The API preview cache is keyed by the request parameters. Change a
    // client-side revision so the next preview cannot reuse an old mesh after
    // a geometry rebuild.
    try {
      sessionStorage.setItem('boxed3d_preview_revision', String(Date.now()));
    } catch {
      // Session storage can be unavailable in private browsing.
    }
  };

  const refreshAppBtn = document.createElement('button');
  refreshAppBtn.type = 'button';
  refreshAppBtn.textContent = '↻ Clear cache & refresh';
  refreshAppBtn.title = 'Clear browser preview cache and reload the app';
  refreshAppBtn.style.cssText = [
    'width:100%', 'height:26px', 'font-size:10px', 'font-weight:600',
    'border:1px solid var(--border)', 'background:#f8fafc', 'color:#475569',
    'border-radius:4px', 'cursor:pointer', 'padding:0 5px',
  ].join(';');

  refreshAppBtn.addEventListener('click', async () => {
    refreshAppBtn.disabled = true;
    refreshAppBtn.textContent = 'Clearing and refreshing...';
    try {
      // CacheStorage can hang in an in-app browser while a preview request is
      // still active. Never let that optional cleanup block the actual reload.
      await Promise.race([
        clearBrowserCache(),
        new Promise<void>(resolve => window.setTimeout(resolve, 800)),
      ]);
    } finally {
      const url = new URL(window.location.href);
      url.searchParams.set('_refresh', String(Date.now()));
      // Changing _refresh guarantees a fresh document and a new preview
      // revision even when CacheStorage is unavailable or times out.
      window.location.replace(url.toString());
    }
  });

  // TASK 28: the segmented switch goes on its own row, above Reset All. Both options stay
  // visible at full width, which is the whole point of the segmented design.
  buttonRow.appendChild(segmented);
  buttonRow.appendChild(qualityLabel);
  buttonRow.appendChild(qualityRow);
  buttonRow.appendChild(resetAll);
  buttonRow.appendChild(refreshAppBtn);

  const restartApiBtn = document.createElement('button');
  restartApiBtn.type = 'button';
  restartApiBtn.textContent = '↻ Restart API & ports';
  restartApiBtn.title = 'Local development only: stop the API on port 38592 and start it cleanly';
  restartApiBtn.style.cssText = refreshAppBtn.style.cssText;
  restartApiBtn.addEventListener('click', async () => {
    if (!window.confirm('Restart the local API and its port now?')) return;
    restartApiBtn.disabled = true;
    refreshAppBtn.disabled = true;
    restartApiBtn.textContent = 'Restarting API...';
    try {
      await fetch(`${API_BASE}/api/dev/restart-api`, {
        method: 'POST',
        headers: apiAuthHeaders({ 'Content-Type': 'application/json' }),
        cache: 'no-store',
      });
      // The old process exits immediately after acknowledging the request.
      // Wait for the replacement process before reloading the document.
      const deadline = Date.now() + 15000;
      while (Date.now() < deadline) {
        try {
          const health = await fetch(`${API_BASE}/api/health`, { cache: 'no-store' });
          if (health.ok) break;
        } catch { /* API is between processes. */ }
        await new Promise(resolve => window.setTimeout(resolve, 500));
      }
    } catch {
      // A connection reset is expected while the old API exits; still reload.
    } finally {
      const url = new URL(window.location.href);
      url.searchParams.set('_restart', String(Date.now()));
      window.location.replace(url.toString());
    }
  });
  buttonRow.appendChild(restartApiBtn);
  headerZone.appendChild(buttonRow);

  sidebarEl.appendChild(headerZone);
  
  const scrollZone = document.createElement('div');
  scrollZone.style.cssText = `
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 15px;
    /* Custom scrollbar styling */
  `;

  // ---- Compact Single-Line Preset Manager (36px Toolbar with 5px vertical padding) ----
  const presetWrap = document.createElement('div');
  presetWrap.className = 'preset-manager-toolbar';
  presetWrap.style.cssText = `
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    padding: 0 8px;
    background: #f8fafc;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    box-sizing: border-box;
    width: 100%;
    height: 36px;
    overflow: hidden;
  `;

  const presetLabel = document.createElement('span');
  presetLabel.style.cssText = 'font-size:11px;font-weight:700;color:#334155;white-space:nowrap;display:inline-flex;align-items:center;gap:4px;height:26px;line-height:26px;flex-shrink:0;margin:0;';
  presetLabel.innerHTML = '💾 <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.03em;">Preset:</span>';
  presetWrap.appendChild(presetLabel);

  const presetSelect = document.createElement('select');
  presetSelect.id = 'preset-select';
  presetSelect.style.cssText = `
    flex: 1;
    min-width: 0;
    height: 26px;
    line-height: 24px;
    padding: 0 18px 0 8px;
    font-size: 11px;
    border: 1px solid #94a3b8;
    border-radius: 4px;
    background: #ffffff;
    color: #0f172a;
    font-family: var(--mono-font);
    font-weight: 500;
    outline: none;
    cursor: pointer;
    text-overflow: ellipsis;
    box-sizing: border-box;
    margin: 0;
    vertical-align: middle;
  `;

  const saveBtn = document.createElement('button');
  saveBtn.id = 'save-custom-btn';
  saveBtn.type = 'button';
  saveBtn.title = 'Save current settings as a new preset';
  saveBtn.innerHTML = '💾+';
  saveBtn.style.cssText = `
    height: 26px;
    width: 28px;
    min-width: 28px;
    padding: 0;
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid #16a34a;
    background: #16a34a;
    color: #ffffff;
    border-radius: 4px;
    cursor: pointer;
    font-family: var(--mono-font);
    transition: all 0.15s;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    box-sizing: border-box;
    flex-shrink: 0;
  `;

  const deleteBtn = document.createElement('button');
  deleteBtn.id = 'delete-preset-btn';
  deleteBtn.type = 'button';
  deleteBtn.title = 'Delete the selected preset';
  deleteBtn.innerHTML = '🗑️';
  deleteBtn.style.cssText = `
    display: none;
    height: 26px;
    width: 28px;
    min-width: 28px;
    padding: 0;
    margin: 0;
    font-size: 11px;
    border: 1px solid #ef4444;
    background: #ffffff;
    color: #ef4444;
    border-radius: 4px;
    cursor: pointer;
    font-family: var(--mono-font);
    transition: all 0.15s;
    align-items: center;
    justify-content: center;
    line-height: 1;
    box-sizing: border-box;
    flex-shrink: 0;
  `;

  // LocalStorage storage helper
  const STORAGE_KEY = `b3d_user_presets_${module.id}`;

  const loadUserPresets = (): Array<{ id: string; name: string; timestamp: number; params: Record<string, any> }> => {
    try {
      const json = localStorage.getItem(STORAGE_KEY);
      return json ? JSON.parse(json) : [];
    } catch {
      return [];
    }
  };

  const saveUserPresets = (presets: any[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
    } catch {}
  };

  const updateDeleteButtonState = () => {
    const isCustom = presetSelect.value !== 'default' && !presetSelect.value.startsWith('builtin_');
    deleteBtn.style.display = isCustom ? 'inline-flex' : 'none';
  };

  const refreshPresetDropdown = (selectedId?: string) => {
    presetSelect.innerHTML = '';
    
    // Default option
    const defOpt = document.createElement('option');
    defOpt.value = 'default';
    defOpt.textContent = '-- Default --';
    presetSelect.appendChild(defOpt);

    const userPresets = loadUserPresets();
    if (userPresets.length > 0) {
      const group = document.createElement('optgroup');
      group.label = 'My Saved Presets';
      for (const p of userPresets) {
        const opt = document.createElement('option');
        opt.value = p.id;
        opt.textContent = `⭐ ${p.name}`;
        group.appendChild(opt);
      }
      presetSelect.appendChild(group);
    }

    if (selectedId) {
      presetSelect.value = selectedId;
    } else {
      presetSelect.value = 'default';
    }

    updateDeleteButtonState();
  };

  // Action: Apply selected preset
  presetSelect.addEventListener('change', () => {
    const val = presetSelect.value;
    updateDeleteButtonState();

    if (val === 'default') {
      // Reset all sliders to default
      for (const s of module.sliders) {
        const sliderEl = scrollZone.querySelector(`.control-group[data-slider-key="${s.key}"]`);
        if (sliderEl) {
          const range = sliderEl.querySelector('input[type="range"]') as HTMLInputElement;
          const num = sliderEl.querySelector('input[type="number"]') as HTMLInputElement;
          if (range) range.value = String(s.default);
          if (num) num.value = String(s.default);
        }
        if (onSliderChange) onSliderChange(s.key, s.default);
      }
    } else {
      const presets = loadUserPresets();
      const target = presets.find(p => p.id === val);
      if (target && target.params) {
        for (const [k, v] of Object.entries(target.params)) {
          // Find matching slider element
          const sliderEl = scrollZone.querySelector(`.control-group[data-slider-key="${k}"]`);
          if (sliderEl) {
            const range = sliderEl.querySelector('input[type="range"]') as HTMLInputElement;
            const num = sliderEl.querySelector('input[type="number"]') as HTMLInputElement;
            if (range) range.value = String(v);
            if (num) num.value = String(v);
          }

          // Trigger callback
          if (onSliderChange) onSliderChange(k, v as any);
        }
      }
    }
  });

  // Action: Save new preset
  saveBtn.addEventListener('click', () => {
    const name = prompt('Enter a name for the new preset:', `Preset ${new Date().toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}`);
    if (!name || !name.trim()) return;

    // Collect current values of sliders
    const currentParams: Record<string, any> = {};
    for (const s of module.sliders) {
      const sliderEl = scrollZone.querySelector(`.control-group[data-slider-key="${s.key}"]`);
      if (sliderEl) {
        const range = sliderEl.querySelector('input[type="range"]') as HTMLInputElement;
        if (range) {
          currentParams[s.key] = parseFloat(range.value);
        }
      }
    }

    const presets = loadUserPresets();
    const newPreset = {
      id: 'preset_' + Date.now(),
      name: name.trim(),
      timestamp: Date.now(),
      params: currentParams
    };

    presets.push(newPreset);
    saveUserPresets(presets);
    refreshPresetDropdown(newPreset.id);
  });

  // Action: Delete selected preset
  deleteBtn.addEventListener('click', () => {
    const val = presetSelect.value;
    if (val === 'default') return;

    const presets = loadUserPresets();
    const found = presets.find(p => p.id === val);
    const pName = found ? found.name : 'this preset';

    if (confirm(`Delete preset "${pName}"?`)) {
      const filtered = presets.filter(p => p.id !== val);
      saveUserPresets(filtered);
      refreshPresetDropdown('default');
    }
  });

  presetWrap.appendChild(presetSelect);
  presetWrap.appendChild(saveBtn);
  presetWrap.appendChild(deleteBtn);

  // Initialize dropdown options
  refreshPresetDropdown();

  scrollZone.appendChild(presetWrap);

  // Render module.selects (Searchable Combobox for quick searching 1,000+ primitives)
  if (module.selects && module.selects.length > 0) {
    for (const sel of module.selects) {
      const groupEl = document.createElement('div');
      groupEl.className = 'control-group select-control-group';
      groupEl.style.cssText = 'margin-bottom: 15px; width: 100%; position: relative; padding-bottom: 12px; border-bottom: 1px dashed var(--border);';

      const label = document.createElement('label');
      label.textContent = sel.label;
      label.style.cssText = 'font-size:11px;font-weight:700;color:var(--text);display:block;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.04em;';
      groupEl.appendChild(label);

      // Searchable Combobox Trigger Button & Card
      const comboboxWrap = document.createElement('div');
      comboboxWrap.style.cssText = 'position:relative;width:100%;';

      const triggerBtn = document.createElement('button');
      triggerBtn.type = 'button';
      triggerBtn.style.cssText = `
        width: 100%;
        padding: 8px 10px;
        box-sizing: border-box;
        border: 1.5px solid #0284c7;
        border-radius: 6px;
        font-family: var(--mono-font);
        font-size: 11.5px;
        background: #ffffff;
        color: #0f172a;
        cursor: pointer;
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 600;
        box-shadow: 0 1px 3px rgba(0,0,0,0.08);
      `;

      // TASK 19 fix: same restore rule as the sliders - a saved value wins over the default.
      let selectedVal = initialValues?.[sel.key] !== undefined
        ? String(initialValues[sel.key])
        : sel.default;
      let isDropdownOpen = false;

      const getOptionLabel = (val: string) => {
        const found = sel.options.find(o => o.value === val);
        return found ? found.label : val;
      };

      const updateTriggerText = () => {
        const idx = sel.options.findIndex(o => o.value === selectedVal) + 1;
        const total = sel.options.length;
        triggerBtn.innerHTML = `
          <span style="color:#94a3b8;font-weight:600;margin-right:6px;">${idx}/${total}</span>
          <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;margin-right:6px;">${getOptionLabel(selectedVal)}</span>
          <span style="font-size:9px;color:#0284c7;">▼</span>
        `;
      };
      updateTriggerText();

      // Search Dropdown Panel
      const dropdownPanel = document.createElement('div');
      dropdownPanel.style.cssText = `
        display: none;
        position: absolute;
        left: 0;
        top: 100%;
        margin-top: 4px;
        width: 100%;
        max-height: 280px;
        overflow-y: auto;
        background: #ffffff;
        border: 1px solid #94a3b8;
        border-radius: 6px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.15);
        z-index: 9999;
        box-sizing: border-box;
        padding: 6px;
      `;

      // Quick Search Input
      const searchInput = document.createElement('input');
      searchInput.type = 'text';
      searchInput.placeholder = 'Search primitive (e.g. CG_..., Ren, Thread, Hinge)...';
      searchInput.style.cssText = `
        width: 100%;
        padding: 6px 8px;
        box-sizing: border-box;
        border: 1px solid #cbd5e1;
        border-radius: 4px;
        font-family: var(--mono-font);
        font-size: 11px;
        outline: none;
        margin-bottom: 6px;
        background: #f8fafc;
      `;

      const optionsListContainer = document.createElement('div');
      optionsListContainer.style.cssText = 'max-height: 220px; overflow-y: auto; display: flex; flex-direction: column; gap: 2px;';

      // The index is FIXED by position in the source list, not by the currently
      // displayed order - so that while typing a search the number still identifies that
      // primitive and the user can count how many there are.
      const orderIndex = new Map<string, number>();
      sel.options.forEach((o, i) => orderIndex.set(o.value, i + 1));

      // Function to render items matching search filter
      const renderFilteredOptions = (filterText = '') => {
        optionsListContainer.innerHTML = '';
        const q = filterText.toLowerCase().trim();

        // Group items only when the options actually declare a group. A list with no
        // groups renders as one flat run of rows, with no header above them.
        const hasGroups = sel.options.some(o => o.group);
        const groupsMap = new Map<string, typeof sel.options>();
        for (const opt of sel.options) {
          if (q && !opt.label.toLowerCase().includes(q) && !opt.value.toLowerCase().includes(q) && !(opt.group || '').toLowerCase().includes(q)) {
            continue;
          }
          const grp = opt.group || 'All Items';
          if (!groupsMap.has(grp)) groupsMap.set(grp, []);
          groupsMap.get(grp)!.push(opt);
        }

        if (groupsMap.size === 0) {
          const emptyMsg = document.createElement('div');
          emptyMsg.style.cssText = 'padding:8px;font-size:10px;color:#94a3b8;text-align:center;';
          emptyMsg.textContent = 'No matching primitives found';
          optionsListContainer.appendChild(emptyMsg);
          return;
        }

        for (const [grpName, opts] of groupsMap.entries()) {
          if (hasGroups) {
            const header = document.createElement('div');
            header.style.cssText = 'font-size:9.5px;font-weight:700;color:#64748b;padding:4px 6px;text-transform:uppercase;letter-spacing:0.04em;background:#f1f5f9;border-radius:3px;margin:2px 0;';
            header.textContent = grpName;
            optionsListContainer.appendChild(header);
          }

          for (const opt of opts) {
            const item = document.createElement('div');
            item.style.cssText = `
              padding: 5px 8px;
              font-size: 11px;
              cursor: pointer;
              border-radius: 3px;
              font-family: var(--mono-font);
              color: ${opt.value === selectedVal ? '#0284c7' : '#1e293b'};
              font-weight: ${opt.value === selectedVal ? '700' : '400'};
              background: ${opt.value === selectedVal ? '#e0f2fe' : 'transparent'};
              transition: all 0.1s;
            `;

            item.addEventListener('mouseenter', () => {
              if (opt.value !== selectedVal) item.style.background = '#f1f5f9';
            });
            item.addEventListener('mouseleave', () => {
              if (opt.value !== selectedVal) item.style.background = 'transparent';
            });

            item.addEventListener('click', () => {
              selectedVal = opt.value;
              updateTriggerText();
              dropdownPanel.style.display = 'none';
              isDropdownOpen = false;
              updateSliderVisibility(selectedVal);
              if (onSliderChange) {
                (onSliderChange as any)(sel.key, selectedVal);
              }
            });

            // The index is kept separate from the label so it is easy to count and does not blend into the description.
            const num = document.createElement('span');
            num.textContent = String(orderIndex.get(opt.value) ?? 0);
            num.style.cssText = `
              display: inline-block;
              min-width: 18px;
              text-align: right;
              margin-right: 6px;
              color: ${opt.value === selectedVal ? '#0369a1' : '#94a3b8'};
              font-weight: 600;
            `;
            item.textContent = '';
            item.appendChild(num);
            item.appendChild(document.createTextNode(opt.label));
            optionsListContainer.appendChild(item);
          }
        }
      };

      searchInput.addEventListener('input', () => {
        renderFilteredOptions(searchInput.value);
      });

      // Filter slider visibility based on active option's sliders list
      const updateSliderVisibility = (selectedVal: string) => {
        const activeOpt = sel.options.find(o => o.value === selectedVal);
        if (activeOpt && (activeOpt as any).sliders && (activeOpt as any).sliders.length > 0) {
          const activeKeys = new Set<string>((activeOpt as any).sliders.map((s: any) => typeof s === 'string' ? s : (s.key || s.id)));
          scrollZone.querySelectorAll<HTMLElement>('.control-group[data-slider-key]').forEach(group => {
            const key = group.dataset.sliderKey;
            if (key) {
              group.style.display = activeKeys.has(key) ? 'block' : 'none';
            }
          });
        } else {
          scrollZone.querySelectorAll<HTMLElement>('.control-group[data-slider-key]').forEach(group => {
            group.style.display = 'block';
          });
        }
      };

      triggerBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        isDropdownOpen = !isDropdownOpen;
        dropdownPanel.style.display = isDropdownOpen ? 'block' : 'none';
        if (isDropdownOpen) {
          searchInput.value = '';
          renderFilteredOptions('');
          setTimeout(() => searchInput.focus(), 50);
        }
      });

      document.addEventListener('click', () => {
        if (isDropdownOpen) {
          dropdownPanel.style.display = 'none';
          isDropdownOpen = false;
        }
      });

      dropdownPanel.addEventListener('click', (e) => e.stopPropagation());

      // Initial filter on render
      setTimeout(() => updateSliderVisibility(selectedVal), 0);

      dropdownPanel.appendChild(searchInput);
      dropdownPanel.appendChild(optionsListContainer);
      comboboxWrap.appendChild(triggerBtn);
      comboboxWrap.appendChild(dropdownPanel);
      groupEl.appendChild(comboboxWrap);
      scrollZone.appendChild(groupEl);
    }
  }

  // Swing badge has three independent strap inscriptions.
  if (module.id === 'swing_badge_keychain' && module.textParams) {
    const strapSection = document.createElement('div');
    strapSection.style.cssText = 'margin-bottom:15px;padding-bottom:12px;border-bottom:1px dashed var(--border);';
    for (const textParam of module.textParams.filter(t => t.visible !== false)) {
      const group = document.createElement('div');
      group.className = 'control-group';
      group.style.cssText = 'margin-bottom:10px;width:100%;display:flex;flex-direction:column;gap:5px;';
      const label = document.createElement('label');
      label.textContent = textParam.label;
      label.style.cssText = 'font-size:11px;font-weight:600;color:var(--text);display:block;';
      const input = document.createElement('textarea');
      input.rows = 2;
      input.value = String((initialValues && initialValues[textParam.key]) ?? textParam.default ?? '');
      input.style.cssText = 'width:100%;padding:7px 10px;box-sizing:border-box;border:1px solid #94a3b8;border-radius:4px;font-family:var(--mono-font);font-size:13px;background:#ffffff;color:#0f172a;font-weight:600;outline:none;resize:vertical;';
      input.addEventListener('change', () => {
        const value = input.value.trim() || String(textParam.default ?? '');
        input.value = value;
        onSliderChange?.(textParam.key, value);
      });
      group.appendChild(label);
      group.appendChild(input);
      strapSection.appendChild(group);
    }
    scrollZone.appendChild(strapSection);
  }

  // If keycap_clicker, name_clicker, text_keychain or linked_key_chain module: Add Embossed Text and Font Family at the VERY TOP of parameters
  // Gate on the SCHEMA, not a hardcoded list of ids: any module that declares textParams
  // gets this panel. A cloned module (e.g. linked_key_chain_textured) used to lose the
  // text box silently because only the original id was listed here.
  if (module.id !== 'swing_badge_keychain' && module.textParams && module.textParams.some(t => t.visible !== false)) {
    const customSection = document.createElement('div');
    customSection.style.cssText = 'margin-bottom:15px;padding-bottom:12px;border-bottom:1px dashed var(--border);';

    // 1. Text input + Update button
    const textGroup = document.createElement('div');
    textGroup.className = 'control-group';
    textGroup.style.cssText = 'margin-bottom:12px;width:100%;display:flex;flex-direction:column;gap:6px;';

    const textLabel = document.createElement('label');
    textLabel.textContent = module.id === 'text_bead_50'
      ? 'Fixed Text (locked)'
      : module.id === 'text_keychain'
      ? 'Keychain Text Content (newline supported)'
      : module.id === 'text_ribbon_3line'
      ? 'Three Text Rows (one row per line)'
      : (module.id === 'name_clicker'
          ? 'Custom Name (Name Text)'
          : (module.id.startsWith('linked_key_chain') ? 'Articulated Keycap Legend (auto key count)' : '3D Embossed Text (auto key count)'));
    textLabel.style.cssText = 'font-size:11px;font-weight:600;color:var(--text);display:block;';
    textGroup.appendChild(textLabel);

    const defaultText = module.id === 'text_keychain'
      ? 'ALEX'
      : module.id === 'text_ribbon_3line'
      ? String(module.textParams?.find(t => t.key === 'text')?.default ?? 'TRI ÂN QUÁ KHỨ\nKIẾN TẠO TƯƠNG LAI\nVỮNG BƯỚC VƯƠN XA')
      : (module.id.startsWith('linked_key_chain') ? 'ZEN' : 'BOXED3D');

    // Older saved states could flatten the three-line value because this module
    // was mistakenly rendered as a single-line input. Restore the known row
    // boundaries before putting the value back into the editor.
    const restoreRibbonRows = (value: string): string => {
      if (module.id !== 'text_ribbon_3line' || value.includes('\n')) return value;
      const second = 'KIẾN TẠO';
      const third = 'VỮNG BƯỚC';
      const secondAt = value.indexOf(second);
      const thirdAt = value.indexOf(third);
      if (secondAt > 0 && thirdAt > secondAt) {
        return `${value.slice(0, secondAt)}\n${value.slice(secondAt, thirdAt)}\n${value.slice(thirdAt)}`;
      }
      return value;
    };

    const initText = module.id === 'text_bead_50'
      ? '50'
      : (initialValues && typeof initialValues['text'] === 'string' && initialValues['text'].length > 0)
      ? restoreRibbonRows(initialValues['text'])
      : ((typeof (window as any).ModuleFactory !== 'undefined' && (window as any).ModuleFactory.getStringParam)
          ? restoreRibbonRows((window as any).ModuleFactory.getStringParam('text', defaultText))
          : defaultText);

    let textInput: HTMLInputElement | HTMLTextAreaElement;
    if (module.id === 'text_keychain' || module.id === 'text_ribbon_3line') {
      const textarea = document.createElement('textarea');
      textarea.rows = module.id === 'text_ribbon_3line' ? 3 : 2;
      textarea.value = initText;
      textarea.style.cssText = 'width:100%;padding:7px 10px;box-sizing:border-box;border:1px solid #94a3b8;border-radius:4px;font-family:var(--mono-font);font-size:13px;background:#ffffff;color:#0f172a;font-weight:600;outline:none;resize:vertical;';
      textInput = textarea;
    } else {
      const input = document.createElement('input');
      input.type = 'text';
      input.value = initText;
      input.readOnly = module.id === 'text_bead_50';
      input.setAttribute('aria-readonly', module.id === 'text_bead_50' ? 'true' : 'false');
      input.maxLength = module.id === 'name_clicker' ? 16 : 12;
      input.style.cssText = 'width:100%;padding:7px 10px;box-sizing:border-box;border:1px solid #94a3b8;border-radius:4px;font-family:var(--mono-font);font-size:13px;background:#ffffff;color:#0f172a;font-weight:600;outline:none;';
      textInput = input;
    }

    const updateTextBtn = document.createElement('button');
    updateTextBtn.textContent = module.id === 'text_bead_50' ? 'Text fixed to 50' : 'Update 3D Text';
    if (module.id === 'text_bead_50') {
      updateTextBtn.disabled = true;
      updateTextBtn.style.cursor = 'not-allowed';
    }
    updateTextBtn.style.cssText = 'width:100%;padding:7px 12px;font-size:11px;font-weight:600;border:1px solid #0f172a;background:#0f172a;color:#ffffff;border-radius:4px;cursor:pointer;font-family:var(--mono-font);transition:all 0.15s;box-sizing:border-box;';

    const doUpdateText = () => {
      let val = textInput.value;
      if (!val || !val.trim()) val = defaultText;
      textInput.value = val;
      if (onSliderChange) {
        // Set the DERIVED keyCount BEFORE the text itself. The text callback applies the change
        // immediately (see main.ts), so if keyCount were sent second the preview would run with
        // the new text and the STALE key count. Order matters here; it did not before, when both
        // values simply accumulated as pending edits for one later Update.
        if (module.sliders.some(sl => sl.key === 'keyCount')) {
          onSliderChange('keyCount', val.length);
        }
        (onSliderChange as any)('text', val);
      }
    };

    updateTextBtn.addEventListener('click', doUpdateText);
    textInput.addEventListener('keydown', (event) => {
      const e = event as KeyboardEvent;
      const multiline = module.id === 'text_keychain' || module.id === 'text_ribbon_3line';
      if (e.key === 'Enter' && (!multiline || e.ctrlKey)) {
        e.preventDefault();
        doUpdateText();
      }
    });

    textGroup.appendChild(textInput);
    textGroup.appendChild(updateTextBtn);
    customSection.appendChild(textGroup);

    // 2. Font selector with 20 diverse font styles & Hover Preview Hint
    const fontGroup = document.createElement('div');
    fontGroup.className = 'control-group';
    fontGroup.style.cssText = 'margin-bottom:10px;width:100%;position:relative;';

    const fontLabel = document.createElement('label');
    fontLabel.textContent = 'Font Family (hover for preview)';
    fontLabel.style.cssText = 'font-size:11px;color:var(--text-muted);display:block;margin-bottom:6px;';
    fontGroup.appendChild(fontLabel);

    const fontSelect = document.createElement('select');
    fontSelect.style.cssText = 'width:100%;padding:6px 10px;box-sizing:border-box;border:1px solid var(--border);border-radius:4px;font-family:var(--mono-font);font-size:12px;background:var(--bg-input);color:var(--text);cursor:pointer;';

    const fonts = [
      { id: 'roboto',     name: 'Roboto Bold (Square / Clean Standard)', tag: 'Clean Standard', fontCss: 'Arial, sans-serif' },
      { id: 'montserrat', name: 'Montserrat Bold (Modern / Modern Geo)', tag: 'Modern Geo', fontCss: 'Trebuchet MS, sans-serif' },
      { id: 'segoesc',    name: 'Segoe Script (Flowing / Cursive)', tag: 'Cursive Flow', fontCss: 'Segoe Script, cursive' },
      { id: 'segoepr',    name: 'Segoe Print (Handwritten)', tag: 'Handwritten', fontCss: 'Segoe Print, cursive' },
      { id: 'brush',      name: 'Brush Script (Brush Art)', tag: 'Brush Art', fontCss: 'Brush Script MT, cursive' },
      { id: 'corsiva',    name: 'Monotype Corsiva (Calligraphy)', tag: 'Calligraphy', fontCss: 'Monotype Corsiva, cursive' },
      { id: 'vladimir',   name: 'Vladimir Script (Royal Script)', tag: 'Royal Script', fontCss: 'Segoe Script, cursive' },
      { id: 'frscript',   name: 'French Script (Elegant Flow)', tag: 'Elegant Flow', fontCss: 'French Script MT, cursive' },
      { id: 'scriptbl',   name: 'Script Bold (Heavy Script)', tag: 'Heavy Script', fontCss: 'Script MT Bold, cursive' },
      { id: 'lhandw',     name: 'Lucida Handwriting (Hand Flow)', tag: 'Hand Flow', fontCss: 'Lucida Handwriting, cursive' },
      { id: 'varela',     name: 'Varela Round (Soft Round)', tag: 'Soft Round', fontCss: 'Segoe UI, sans-serif' },
      { id: 'rubik',      name: 'Rubik Bold (Chubby Round)', tag: 'Chubby Round', fontCss: 'Impact, sans-serif' },
      { id: 'comic',      name: 'Comic Bold (Cute Kid)', tag: 'Cute Kid', fontCss: 'Comic Sans MS, cursive' },
      { id: 'bungee',     name: 'Bungee (Heavy Block)', tag: 'Heavy Block', fontCss: 'Impact, sans-serif' },
      { id: 'impact',     name: 'Impact (Ultra Heavy)', tag: 'Ultra Heavy', fontCss: 'Impact, sans-serif' },
      { id: 'orbitron',   name: 'Orbitron (Sci-Fi Tech)', tag: 'Sci-Fi Tech', fontCss: 'Courier New, monospace' },
      { id: 'stencil',    name: 'Stencil (Military Cut)', tag: 'Military Cut', fontCss: 'Impact, sans-serif' },
      { id: 'times',      name: 'Times (Classic Serif)', tag: 'Classic Serif', fontCss: 'Times New Roman, serif' },
      { id: 'monospace',  name: 'Monospace (Fixed Width)', tag: 'Fixed Width', fontCss: 'Courier New, monospace' },
      { id: 'arial',      name: 'Arial Bold (Universal)', tag: 'Universal', fontCss: 'Arial, sans-serif' },
      { id: 'georgia',    name: 'Georgia (Serif Light)', tag: 'Serif Light', fontCss: 'Georgia, serif' },
      { id: 'verdana',    name: 'R Verdana (Easy Reading / Wide Sans)', tag: 'Wide Sans', fontCss: 'Verdana, sans-serif' },
      { id: 'trebuchet',  name: 'Trebuchet MS (Dynamic)', tag: 'Dynamic', fontCss: 'Trebuchet MS, sans-serif' },
      { id: 'tahoma',     name: 'Tahoma Bold (Compact)', tag: 'Compact', fontCss: 'Tahoma, sans-serif' },
      { id: 'courier',    name: 'Courier New (Retro Typewriter)', tag: 'Retro Typewriter', fontCss: 'Courier New, monospace' },
      { id: 'century',    name: 'Century Gothic (Clean Circle)', tag: 'Clean Circle', fontCss: 'Century Gothic, sans-serif' },
      { id: 'calibri',    name: 'Calibri (Soft Modern)', tag: 'Soft Modern', fontCss: 'Calibri, sans-serif' },
      { id: 'bahnschrift',name: 'Bahnschrift (Heavy Industrial)', tag: 'Heavy Industrial', fontCss: 'Arial Black, sans-serif' },
    ];
    if (module.id === 'text_bead') {
      fonts.push(
        { id: 'freckleface', name: 'Freckle Face (Playful Hand-Drawn)', tag: 'Playful Kid', fontCss: '"Freckle Face", sans-serif' },
        { id: 'markoone', name: 'Marko One (Storybook)', tag: 'Storybook', fontCss: '"Marko One", serif' },
        { id: 'lilitaone', name: 'Lilita One (Chunky Rounded)', tag: 'Chunky Bubble', fontCss: '"Lilita One", sans-serif' },
        { id: 'coiny', name: 'Coiny (Soft Bubble)', tag: 'Soft Bubble', fontCss: 'Coiny, sans-serif' },
        { id: 'titanone', name: 'Titan One (Extra Thick)', tag: 'Extra Thick', fontCss: '"Titan One", sans-serif' },
        { id: 'bubblegum', name: 'Bubblegum Sans (Kid Bubble)', tag: 'Kid Bubble', fontCss: '"Bubblegum Sans", sans-serif' },
        { id: 'sniglet', name: 'Sniglet ExtraBold (Round Kid)', tag: 'Round Kid', fontCss: 'Sniglet, sans-serif' },
      );
      if (!document.getElementById('text-bead-font-face-definitions')) {
        const style = document.createElement('style');
        style.id = 'text-bead-font-face-definitions';
        style.textContent = [
          ['Freckle Face', 'freckleface', 'FreckleFace-Regular.ttf'],
          ['Marko One', 'markoone', 'MarkoOne-Regular.ttf'],
          ['Lilita One', 'lilitaone', 'LilitaOne-Regular.ttf'],
          ['Coiny', 'coiny', 'Coiny-Regular.ttf'],
          ['Titan One', 'titanone', 'TitanOne-Regular.ttf'],
          ['Bubblegum Sans', 'bubblegumsans', 'BubblegumSans-Regular.ttf'],
          ['Sniglet', 'sniglet', 'Sniglet-ExtraBold.ttf'],
        ].map(([family, folder, file]) =>
          `@font-face{font-family:"${family}";src:url("/fonts/${folder}/${file}") format("truetype");font-display:swap;font-weight:100 900;}`
        ).join('\n');
        document.head.appendChild(style);
      }
    }

    // Custom Font Selector UI with active open list and hover preview popup
    let isOpen = false;
    let selectedFontId = (initialValues && typeof initialValues['fontStyle'] === 'string' && initialValues['fontStyle'].length > 0)
      ? initialValues['fontStyle']
      : 'roboto';

    const customPickerWrap = document.createElement('div');
    customPickerWrap.style.cssText = 'width:100%;position:relative;user-select:none;';

    const fontTriggerBtn = document.createElement('button');
    fontTriggerBtn.type = 'button';
    fontTriggerBtn.style.cssText = 'width:100%;padding:7px 10px;box-sizing:border-box;border:1px solid var(--border);border-radius:4px;font-family:var(--mono-font);font-size:12px;background:var(--bg-input);color:var(--text);cursor:pointer;display:flex;justify-content:space-between;align-items:center;text-align:left;';
    
    const updateTriggerText = () => {
      const curObj = fonts.find(f => f.id === selectedFontId) || fonts[0]!;
      fontTriggerBtn.innerHTML = `<span>${curObj.name}</span><span style="font-size:10px;color:var(--text-muted);margin-left:6px;">▼</span>`;
    };
    updateTriggerText();

    const fontDropdownList = document.createElement('div');
    fontDropdownList.style.cssText = `
      display: none;
      position: absolute;
      left: 0;
      top: 100%;
      margin-top: 4px;
      width: 100%;
      max-height: 220px;
      overflow-y: auto;
      background: #ffffff;
      border: 1px solid #94a3b8;
      border-radius: 6px;
      box-shadow: 0 10px 25px rgba(0,0,0,0.15);
      z-index: 999;
      box-sizing: border-box;
    `;

    // Floating Preview Popup Card (Large Prominent Preview Card - 2x Size)
    const previewHintCard = document.createElement('div');
    previewHintCard.id = 'font-preview-popup';
    previewHintCard.style.cssText = `
      display: none;
      position: fixed;
      width: 380px;
      padding: 16px 20px;
      background: #0f172a;
      color: #ffffff;
      border: 2px solid #38bdf8;
      border-radius: 10px;
      box-shadow: 0 15px 35px rgba(0,0,0,0.5);
      z-index: 99999;
      box-sizing: border-box;
      pointer-events: none;
    `;
    document.body.appendChild(previewHintCard);

    const showPreviewPopup = (fontId: string, clientX: number, clientY: number) => {
      const fObj = fonts.find(f => f.id === fontId) || fonts[0]!;
      const previewText = textInput.value || 'BOXED3D';
      previewHintCard.innerHTML = `
        <div style="font-size:12px;color:#94a3b8;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.08em;display:flex;justify-content:space-between;align-items:center;">
          <span>FONT PREVIEW HINT</span>
          <span style="color:#38bdf8;font-weight:bold;font-size:12px;">${fObj.tag}</span>
        </div>
        <div style="font-size:44px;font-weight:bold;font-family:${fObj.fontCss};color:#f8fafc;word-break:break-all;line-height:1.2;text-align:center;padding:16px 10px;background:#1e293b;border-radius:8px;margin-top:6px;box-shadow:inset 0 2px 6px rgba(0,0,0,0.3);">
          ${previewText}
        </div>
      `;

      let x = clientX + 20;
      let y = clientY - 60;
      if (x + 390 > window.innerWidth) x = clientX - 395;
      if (y + 160 > window.innerHeight) y = window.innerHeight - 170;
      if (y < 10) y = 10;
      previewHintCard.style.left = `${x}px`;
      previewHintCard.style.top = `${y}px`;
      previewHintCard.style.display = 'block';
    };

    const hidePreviewPopup = () => {
      previewHintCard.style.display = 'none';
    };

    fonts.forEach(f => {
      const item = document.createElement('div');
      item.style.cssText = 'padding:7px 10px;font-size:11px;color:#0f172a;cursor:pointer;border-bottom:1px solid #f1f5f9;font-family:var(--mono-font);transition:background 0.1s;';
      item.textContent = f.name;

      item.addEventListener('mouseenter', (e) => {
        item.style.background = '#e2e8f0';
        showPreviewPopup(f.id, e.clientX, e.clientY);
      });

      item.addEventListener('mousemove', (e) => {
        showPreviewPopup(f.id, e.clientX, e.clientY);
      });

      item.addEventListener('mouseleave', () => {
        item.style.background = 'transparent';
        hidePreviewPopup();
      });

      item.addEventListener('click', () => {
        selectedFontId = f.id;
        updateTriggerText();
        fontDropdownList.style.display = 'none';
        isOpen = false;
        hidePreviewPopup();
        if (onSliderChange) {
          (onSliderChange as any)('fontStyle', f.id);
        }
      });

      fontDropdownList.appendChild(item);
    });

    fontTriggerBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      isOpen = !isOpen;
      fontDropdownList.style.display = isOpen ? 'block' : 'none';
      if (!isOpen) hidePreviewPopup();
    });

    document.addEventListener('click', () => {
      if (isOpen) {
        fontDropdownList.style.display = 'none';
        isOpen = false;
        hidePreviewPopup();
      }
    });

    customPickerWrap.appendChild(fontTriggerBtn);
    customPickerWrap.appendChild(fontDropdownList);
    fontGroup.appendChild(customPickerWrap);
    customSection.appendChild(fontGroup);

    scrollZone.appendChild(customSection);
  }

  for (const slider of module.sliders) {
    if (slider.key === 'text' && (module.id === 'keycap_clicker' || module.id === 'name_clicker' || module.id === 'text_keychain')) {
      continue;
    }
    // TASK 19 fix: start from the restored value when there is one, else the schema default.
    const startValue = initialValues?.[slider.key] !== undefined
      ? Number(initialValues[slider.key])
      : slider.default;
    const el = createSlider(slider, startValue, {
      onChange: (value: number) => {
        if (onSliderChange) {
          onSliderChange(slider.key, value);
        }
      },
    });
    el.dataset.sliderKey = slider.key;
    scrollZone.appendChild(el);
  }

  sidebarEl.appendChild(scrollZone);

  // ---- FOOTER ZONE (fixed) ----
  const footerZone = document.createElement('div');
  footerZone.style.cssText = `
    flex-shrink: 0;
    padding: 12px 15px;
    border-top: 1px solid var(--border);
    background: var(--bg-surface);
  `;

  // Segmented Pill for export format: 3MF (default) vs STL
  const formatSelectWrap = document.createElement('div');
  formatSelectWrap.style.cssText = `
    display: flex;
    align-items: center;
    background: #f1f5f9;
    padding: 2px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    margin-bottom: 8px;
    gap: 2px;
  `;

  const btn3mf = document.createElement('button');
  btn3mf.type = 'button';
  btn3mf.textContent = '3MF (Bambu)';
  btn3mf.title = '3MF: Retains multi-part colors & separate objects for Bambu Studio, OrcaSlicer (Recommended)';

  const btnStl = document.createElement('button');
  btnStl.type = 'button';
  btnStl.textContent = 'STL (Standard)';
  btnStl.title = 'STL: Traditional single-color format compatible with all 3D slicers';

  const exportViewBtn = createButton('👁️ Export 3MF View Selection', () => {/* hook set in main.ts */ });
  exportViewBtn.id = 'exportViewBtn';
  exportViewBtn.title = 'WYSIWYG Export — Only export parts currently visible on screen';
  exportViewBtn.style.cssText = `
    width: 100%;
    padding: 8px 12px;
    background: var(--bg-surface, #ffffff);
    color: var(--text, #1e293b);
    border: 1px solid var(--border, #cbd5e1);
    border-radius: 4px;
    cursor: pointer;
    font-size: 10.5px;
    font-weight: 600;
    font-family: var(--mono-font);
    transition: all 0.15s;
    margin-bottom: 6px;
    text-align: center;
  `;
  exportViewBtn.addEventListener('mouseenter', () => exportViewBtn.style.background = '#f1f5f9');
  exportViewBtn.addEventListener('mouseleave', () => exportViewBtn.style.background = 'var(--bg-surface, #ffffff)');

  const exportObjectsBtn = createButton('📦 Export Separate Objects (3MF Layout)', () => {/* hook set in main.ts */ });
  exportObjectsBtn.id = 'exportObjectsBtn';
  exportObjectsBtn.title = 'Export each Object as independent files for easy 3D printing layout';
  exportObjectsBtn.style.cssText = `
    width: 100%;
    padding: 8px 12px;
    background: var(--bg-surface, #ffffff);
    color: var(--text, #1e293b);
    border: 1px solid var(--border, #cbd5e1);
    border-radius: 4px;
    cursor: pointer;
    font-size: 10.5px;
    font-weight: 600;
    font-family: var(--mono-font);
    transition: all 0.15s;
    text-align: center;
  `;
  exportObjectsBtn.addEventListener('mouseenter', () => exportObjectsBtn.style.background = '#f1f5f9');
  exportObjectsBtn.addEventListener('mouseleave', () => exportObjectsBtn.style.background = 'var(--bg-surface, #ffffff)');

  // Export ONLY what is selected in the 3D view / scene tree. The two buttons above export
  // "everything visible" or "every object"; neither can produce a single part, which is what
  // you want when only one piece failed to print.
  const exportSelectedBtn = createButton('🎯 Export Selected Object', () => {/* hook set in main.ts */ });
  exportSelectedBtn.id = 'exportSelectedBtn';
  exportSelectedBtn.title = 'Export only the object(s) currently selected in the 3D view or scene tree';
  // Primary styling: it is the first export action and the most specific one.
  exportSelectedBtn.style.cssText = `
    width: 100%;
    padding: 9px 12px;
    background: var(--accent);
    color: var(--accent-text);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    font-family: var(--mono-font);
    transition: all 0.15s;
    text-align: center;
    margin-bottom: 6px;
  `;
  exportSelectedBtn.addEventListener('mouseenter', () => exportSelectedBtn.style.opacity = '0.9');
  exportSelectedBtn.addEventListener('mouseleave', () => exportSelectedBtn.style.opacity = '1');

  const syncExportFormatUI = () => {
    if (activeExportFormat === '3mf') {
      btn3mf.style.cssText = 'flex: 1; padding: 5px 6px; font-size: 10.5px; font-weight: 700; border-radius: 4px; border: none; background: #ffffff; color: #0284c7; box-shadow: 0 1px 3px rgba(0,0,0,0.1); cursor: pointer; transition: all 0.15s; font-family: var(--mono-font);';
      btnStl.style.cssText = 'flex: 1; padding: 5px 6px; font-size: 10.5px; font-weight: 500; border-radius: 4px; border: none; background: transparent; color: #64748b; cursor: pointer; transition: all 0.15s; font-family: var(--mono-font);';
      exportViewBtn.textContent = '👁️ Export 3MF View Selection';
      exportViewBtn.title = 'WYSIWYG Export — Export 3MF file with Bambu colors for currently visible parts';
      exportObjectsBtn.textContent = '📦 Export Separate Objects (3MF Layout)';
      exportObjectsBtn.title = 'Export each Object as independent 3MF files with color for 3D print layout';
      exportSelectedBtn.textContent = '🎯 Export Selected Object (3MF)';
    } else {
      btnStl.style.cssText = 'flex: 1; padding: 5px 6px; font-size: 10.5px; font-weight: 700; border-radius: 4px; border: none; background: #ffffff; color: #0f172a; box-shadow: 0 1px 3px rgba(0,0,0,0.1); cursor: pointer; transition: all 0.15s; font-family: var(--mono-font);';
      btn3mf.style.cssText = 'flex: 1; padding: 5px 6px; font-size: 10.5px; font-weight: 500; border-radius: 4px; border: none; background: transparent; color: #64748b; cursor: pointer; transition: all 0.15s; font-family: var(--mono-font);';
      exportViewBtn.textContent = '👁️ Export STL View Selection';
      exportViewBtn.title = 'WYSIWYG Export — Export currently visible parts as binary STL';
      exportObjectsBtn.textContent = '📦 Export Separate Objects (STL Layout)';
      exportObjectsBtn.title = 'Export each Object as independent STL files for 3D print layout';
      exportSelectedBtn.textContent = '🎯 Export Selected Object (STL)';
    }
  };

  btn3mf.addEventListener('click', () => {
    setExportFormat('3mf');
    syncExportFormatUI();
  });

  btnStl.addEventListener('click', () => {
    setExportFormat('stl');
    syncExportFormatUI();
  });

  formatSelectWrap.appendChild(btn3mf);
  formatSelectWrap.appendChild(btnStl);
  footerZone.appendChild(formatSelectWrap);
  // Selected-object export sits first: it is the most specific action, and the one reached for
  // when a single part needs reprinting.
  footerZone.appendChild(exportSelectedBtn);
  footerZone.appendChild(exportViewBtn);
  footerZone.appendChild(exportObjectsBtn);

  syncExportFormatUI();

  sidebarEl.appendChild(footerZone);

  // R23-fix: Status bar removed - now using statusLog (fixed at bottom)
  return { root: sidebarEl, status: null as any };  // Backward compat - not used anymore
}
